import type { StationKOT } from '@/types'
import { kotSlipTable, newKotSlip, renderKotSlipText, type KotSlip } from '@/lib/kotSlip'
import { formatTimeWithSecondsHuman } from '@/lib/utils'

/**
 * Client-side builder for an OFFLINE kitchen ticket — produced with no server
 * round-trip when the terminal has lost the backend.
 *
 * The layout itself lives in lib/kotSlip.ts (the structured slip + the 32-col
 * text renderer that mirrors backend/internal/handlers/kot_slip.go). This file
 * only decides routing (category → station) and fills the slip. The kitchen
 * must be able to tell this ticket has not reached the server, so nobody
 * assumes the POS already knows about it — hence the OFFLINE banner.
 */

export interface OfflineStation {
  id: string
  name: string
  /** Matches StationKOT — a cached value outside this union is bad data. */
  outputType: 'kds' | 'printer'
  printLocation: 'kitchen' | 'counter'
  printerTarget: string
}

export interface OfflineKotItem {
  name: string
  quantity: number
  categoryId: string
  special_instructions?: string
}

export interface OfflineKotInput {
  /**
   * What the slip calls this order (F-CTRL-N52).
   *
   * A locally-minted ticket ("OFF-A3-7") for a new offline order, or the real
   * order number when adding a round to an order the server already has.
   *
   * It is deliberately NOT a plausible order number in the new-order case. The
   * previous scheme printed a reserved number from a 9001+ band, which read as
   * an ordinary order number to everyone who saw it — staff quoting it, owners
   * counting the day by it — while being nothing of the sort. A ticket that
   * announces itself is worth more here than one that blends in.
   */
  ticket: string
  tableNumber: string
  serverName: string
  orderTypeLabel: string
  capturedAt: number
  orderNote: string
  items: OfflineKotItem[]
  stations: OfflineStation[]
  categoryToStation: Record<string, string>
}

/** "3:04:12 PM" from epoch ms; empty if unparseable. Mirrors kot_slip.go (F-FE-N122). */
function firedStamp(ms: number): string {
  const d = new Date(ms)
  if (isNaN(d.getTime())) return ''
  return formatTimeWithSecondsHuman(d)
}

/**
 * Deterministic fallback for an unmapped category, mirroring kot.go: prefer a
 * station literally named "Main Kitchen", else the first known one. An unmapped
 * line must still reach somebody rather than disappear off the ticket — a
 * missing line means food that was ordered and never made.
 */
function fallbackStation(stations: OfflineStation[]): OfflineStation | null {
  if (stations.length === 0) return null
  return stations.find((s) => s.name === 'Main Kitchen') ?? stations[0]
}

export function buildOfflineKotSlips(input: OfflineKotInput): StationKOT[] {
  const byId = new Map(input.stations.map((s) => [s.id, s]))
  // Insertion-ordered so slips print in the order lines were rung up.
  const grouped = new Map<string, { station: OfflineStation; items: OfflineKotItem[] }>()

  for (const item of input.items) {
    const mappedId = input.categoryToStation[item.categoryId]
    const station = (mappedId ? byId.get(mappedId) : undefined) ?? fallbackStation(input.stations)
    if (!station) continue
    const entry = grouped.get(station.id) ?? { station, items: [] }
    entry.items.push(item)
    grouped.set(station.id, entry)
  }

  const slips: StationKOT[] = []
  for (const { station, items } of grouped.values()) {
    const slip: KotSlip = newKotSlip('offline', 'KITCHEN ORDER')
    slip.banners.push('*** OFFLINE - NOT SYNCED ***')
    const label = input.orderTypeLabel.trim()
    if (label) slip.banners.push(`>>> ${label} <<<`)
    slip.table = kotSlipTable(input.tableNumber)
    slip.meta.push({ left: `#${input.ticket}`, right: station.name })
    slip.meta.push({ left: input.serverName.trim(), right: firedStamp(input.capturedAt) })
    for (const ln of input.orderNote.trim().split('\n')) {
      const clean = ln.replace(/[ \t\r]+$/, '')
      if (clean) slip.note.push(clean)
    }
    for (const item of items) {
      const si = (item.special_instructions ?? '').trim()
      slip.items.push({ name: item.name, qty: item.quantity, ...(si ? { note: si } : {}) })
    }

    slips.push({
      station_id: station.id,
      station_name: station.name,
      output_type: station.outputType,
      print_location: station.printLocation,
      printer_target: station.printerTarget,
      payload: renderKotSlipText(slip),
      slip,
    })
  }
  return slips
}

/**
 * The chit that tells the kitchen to STOP making something (F-CTRL-N56).
 *
 * A void taken offline is the one case where editing local state is not enough.
 * The order is a draft this terminal owns, so removing the line costs nothing —
 * but the kitchen is holding paper for it and is already cooking. Without a
 * chit, the guest is not charged and the food is made anyway, which is exactly
 * the "food out, nobody billed" hole the reconciliation queue exists to close,
 * arrived at from the other direction.
 *
 * Routed to the SAME station the original line went to, because that is the
 * printer the original ticket came out of. Reuses `newKotSlip('void', …)`, so it
 * renders with the same `*** VOID KOT ***` banner as a server-issued void and a
 * cook does not have to learn a second format.
 */
export function buildOfflineVoidSlips(input: OfflineKotInput): StationKOT[] {
  const byId = new Map(input.stations.map((s) => [s.id, s]))
  const grouped = new Map<string, { station: OfflineStation; items: OfflineKotItem[] }>()

  for (const item of input.items) {
    const mappedId = input.categoryToStation[item.categoryId]
    const station = (mappedId ? byId.get(mappedId) : undefined) ?? fallbackStation(input.stations)
    if (!station) continue
    const entry = grouped.get(station.id) ?? { station, items: [] }
    entry.items.push(item)
    grouped.set(station.id, entry)
  }

  const slips: StationKOT[] = []
  for (const { station, items } of grouped.values()) {
    const slip: KotSlip = newKotSlip('void', '*** VOID KOT ***')
    slip.banners.push('*** OFFLINE - NOT SYNCED ***')
    const label = input.orderTypeLabel.trim()
    if (label) slip.banners.push(`>>> ${label} <<<`)
    slip.table = kotSlipTable(input.tableNumber)
    slip.meta.push({ left: `#${input.ticket}`, right: station.name })
    slip.meta.push({ left: input.serverName.trim(), right: firedStamp(input.capturedAt) })
    // Said plainly. A cook reading this in a rush must not have to infer it
    // from the banner alone.
    slip.note.push('DO NOT MAKE — these items are cancelled')
    for (const item of items) {
      slip.items.push({ name: item.name, qty: item.quantity })
    }

    slips.push({
      station_id: station.id,
      station_name: station.name,
      output_type: station.outputType,
      print_location: station.printLocation,
      printer_target: station.printerTarget,
      payload: renderKotSlipText(slip),
      slip,
    })
  }
  return slips
}
